import { useCallback, useMemo, useRef, useState } from 'react'
import { useQueryClient, useQuery as useBaseQuery } from '@tanstack/react-query'
import { chatSearch } from '@fireflyhealth/api'

/**
 * Hook to search messages across all threads for a user.
 *
 * `search` does nothing until `userId` is known, and changes identity once it is, so an effect
 * that searches on mount re-runs when the member loads.
 */
export const useMessageSearch = (userId: number | undefined) => {
  const queryClient = useQueryClient()
  const [currentQuery, setCurrentQuery] = useState<string>('')
  // Use ref to store the query for the fetch function to access immediately
  const queryRef = useRef<string>('')

  // Query key based only on user_id for caching
  const queryKey = useMemo(() => ['messageSearch', userId], [userId])

  // useQuery with manual triggering via enabled flag
  const { data, isFetching, error, refetch } = useBaseQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await chatSearch.retrieve({
        params: { user_id: String(userId), query: queryRef.current },
      })
      return data
    },
    enabled: false, // Don't auto-fetch, only fetch when search() is called
    staleTime: 0, // Always refetch when search() is called
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
    retry: false,
  })

  // Imperative search function
  const search = useCallback(
    async (searchQuery: string) => {
      if (userId === undefined) {
        return
      }
      queryRef.current = searchQuery
      setCurrentQuery(searchQuery)
      await refetch()
    },
    [userId, refetch],
  )

  // Clear function to reset search state
  const clear = useCallback(() => {
    queryRef.current = ''
    setCurrentQuery('')
    queryClient.setQueryData(queryKey, null)
  }, [queryClient, queryKey])

  return {
    data: data ?? null,
    isFetching,
    isLoading: isFetching,
    error,
    search,
    clear,
    currentQuery,
  }
}
